import { Link } from '@tanstack/react-router';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { getColophonSections } from '@/data/colophon-sections';

// ═══════════════════════════════════════════════════════════
// ColophonContent — Océanne (single source of truth).
// Réutilisé par ColophonModal (variant="modal") + routes/colophon.tsx (variant="page").
//
// Voix Océanne : transparence totale sur typo, palette, principes éditoriaux,
// équipe, accessibilité, stack. E-E-A-T boost pour SEO + crédibilité brand.
//
// Phase 27.85 SSG : data extraite vers src/data/colophon-sections.ts pour
// permettre Worker SSR (Schema.org AboutPage enrichi + HTML template injection).
// ═══════════════════════════════════════════════════════════

interface ColophonContentProps {
  variant: 'modal' | 'page';
  onClose?: () => void;
}

export function ColophonContent({ variant, onClose }: ColophonContentProps) {
  const { lang } = useLang();
  const text = ta(t.colophon, lang);
  const isFr = lang === 'fr';
  const isPage = variant === 'page';

  const sections = getColophonSections(lang);

  return (
    <div className="space-y-12">
      {sections.map((section) => (
        <ScrollReveal key={section.id} as="section" {...{ id: section.id }}>
          <h2 className="mb-3 font-display text-2xl text-balance text-foreground md:text-3xl">
            {section.title}
          </h2>

          {section.kind === 'palette' ? (
            <div className="space-y-4 font-sans text-base text-pretty hyphens-auto text-foreground/80">
              <p>{section.intro}</p>
              <ul className="ml-6 space-y-3 list-disc">
                {section.items.map((item) => (
                  <li key={item.name}>
                    <span
                      className="inline-block h-3 w-3 rounded-full mr-2 align-middle"
                      style={{ backgroundColor: item.hex }}
                      aria-hidden="true"
                    />
                    <strong className="font-bold text-foreground">{item.name}</strong>{' '}
                    <code className="font-mono text-xs">{item.oklch}</code> — {item.role}
                  </li>
                ))}
              </ul>
            </div>
          ) : section.id === 'colophon-stack' ? (
            // Stack technique : dernier paragraphe contient un lien externe vers Intralys
            <div className="space-y-4 font-sans text-base text-pretty hyphens-auto text-foreground/80">
              {section.paragraphs.slice(0, -1).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p>
                {isFr
                  ? 'Studio : ce site a été conçu et codé par '
                  : 'Studio: this site was designed and coded by '}
                <a
                  href="https://intralys.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 border-b border-primary/60 text-primary hover:text-primary/80 transition-colors"
                >
                  Intralys
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
                {isFr
                  ? ', studio éditorial spécialisé en sites pour conseillers AMF au Québec.'
                  : ', editorial studio specialized in websites for Quebec AMF advisors.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4 font-sans text-base text-pretty hyphens-auto text-foreground/80">
              {section.paragraphs.map((p, i) => (
                <p key={i} className={section.id === 'colophon-a11y' && i === section.paragraphs.length - 1 ? 'italic' : ''}>
                  {p}
                </p>
              ))}
            </div>
          )}
        </ScrollReveal>
      ))}

      {/* CTA "Lire version complète" → page SEO (UNIQUEMENT en modal) */}
      {!isPage && (
        <div className="border-t border-border/60 pt-8">
          <Link
            to="/colophon"
            onClick={onClose}
            className="group inline-flex items-center gap-2 font-sans text-sm font-bold uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
          >
            {text.modal_cta_full}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      )}

      {/* Footer crossover (page SEO seulement) */}
      {isPage && (
        <div className="border-t border-border/60 pt-8 flex flex-wrap items-center justify-between gap-4 text-sm font-sans">
          <Link to="/" className="text-foreground/70 hover:text-primary transition-colors">
            {isFr ? '← Retour à l\'accueil' : '← Back to home'}
          </Link>
          <div className="flex gap-6">
            <Link to="/lexique" className="text-foreground/70 hover:text-primary transition-colors">
              {isFr ? 'Lexique →' : 'Glossary →'}
            </Link>
            <Link to="/carnet" className="text-foreground/70 hover:text-primary transition-colors">
              {isFr ? 'Carnet de ressources →' : 'Resource book →'}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
